import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileText, Trash2 } from "lucide-react";
import { API_BASE } from "@/lib/api";

const kindOf = (ct = "") => {
  ct = ct.toLowerCase();
  if (ct.includes("pdf")) return "pdf";
  if (ct.startsWith("image/")) return "image";
  if (ct.startsWith("video/")) return "video";
  return "other";
};

function Viewer({ url, kind, nom, onDownload }) {
  if (kind === "other") return (
    <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-500 text-sm p-6 text-center" data-testid="reader-unsupported">
      <FileText className="h-10 w-10 text-slate-300" />
      <p>Ce type de fichier ne peut pas être lu dans le navigateur.</p>
      <Button onClick={onDownload} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="reader-unsupported-download"><Download className="h-4 w-4 mr-1.5" />Télécharger {nom}</Button>
    </div>
  );
  if (!url) return <div className="flex items-center justify-center h-full text-slate-400 text-sm">Chargement…</div>;
  if (kind === "image") return <img src={url} alt={nom} className="w-full h-full object-contain" data-testid="reader-image" />;
  if (kind === "video") return <video src={url} controls className="w-full h-full bg-black" data-testid="reader-video" />;
  return <iframe title={nom} src={url} className="w-full h-full" data-testid="reader-pdf" />;
}

export default function AttachmentReader({ resource, onOpenChange, canManage, onDelete }) {
  const atts = resource?.attachments || [];
  const [idx, setIdx] = useState(0);
  const [url, setUrl] = useState(null);
  const att = atts[idx];
  const kind = kindOf(att?.content_type);
  const path = resource && att ? `/resources/${resource.id}/attachment/${idx}` : null;

  useEffect(() => { setIdx(0); }, [resource?.id]);
  useEffect(() => {
    setUrl(null);
    if (!path || kind === "other") return;
    let obj, alive = true;
    fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("omnibat_token")}` } })
      .then((r) => r.blob()).then((b) => { obj = URL.createObjectURL(b); if (alive) setUrl(obj); });
    return () => { alive = false; if (obj) URL.revokeObjectURL(obj); };
  }, [path, kind]);

  const download = async () => {
    let href = url;
    if (!href) {
      const r = await fetch(`${API_BASE}${path}`, { headers: { Authorization: `Bearer ${localStorage.getItem("omnibat_token")}` } });
      href = URL.createObjectURL(await r.blob());
    }
    const a = document.createElement("a"); a.href = href; a.download = att?.nom || "piece-jointe"; a.click();
  };

  return (
    <Dialog open={!!resource} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[95vh] overflow-hidden" data-testid="attachment-reader">
        <DialogHeader><DialogTitle className="font-head truncate pr-6">{resource?.titre}</DialogTitle></DialogHeader>
        <div className="flex flex-col md:flex-row gap-3 min-h-0">
          {atts.length > 1 && (
            <div className="md:w-56 shrink-0 flex md:flex-col gap-1 overflow-x-auto md:overflow-y-auto md:max-h-[72vh]" data-testid="reader-file-list">
              {atts.map((a, i) => (
                <button key={i} onClick={() => setIdx(i)} data-testid={`reader-file-${i}`}
                  className={`text-left text-xs rounded-md px-2 py-1.5 shrink-0 max-w-[200px] md:max-w-none truncate transition-colors ${i === idx ? "bg-brand-600 text-white" : "bg-slate-50 hover:bg-slate-100 text-slate-700"}`}>
                  {a.nom}
                </button>
              ))}
            </div>
          )}
          <div className="flex-1 h-[60vh] md:h-[72vh] rounded-lg border overflow-hidden bg-slate-100">
            {att && <Viewer url={url} kind={kind} nom={att.nom} onDownload={download} />}
          </div>
        </div>
        {url && kind === "pdf" && <a href={url} target="_blank" rel="noreferrer" className="text-xs text-brand-600 hover:underline" data-testid="reader-open-tab">Le document ne s'affiche pas ? Ouvrir dans un nouvel onglet</a>}
        <div className="flex items-center justify-between gap-2 pt-2">
          <p className="text-xs text-slate-500 truncate" data-testid="reader-current-name">{att?.nom} {att?.taille && `(${att.taille})`}</p>
          <div className="flex gap-2 shrink-0">
            {canManage && att && <Button variant="outline" onClick={() => onDelete(resource, idx)} className="text-rose-600" data-testid="reader-delete"><Trash2 className="h-4 w-4 mr-1.5" />Supprimer</Button>}
            <Button onClick={download} disabled={!att} className="bg-brand-600 hover:bg-brand-700 text-white" data-testid="reader-download"><Download className="h-4 w-4 mr-1.5" />Télécharger</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
